import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'ichimoku',
  name: 'Ichimoku Cloud',
  shortName: 'Ichimoku',
  category: 'trend',
  overlay: true,
  params: [
    { key: 'tenkanPeriod', label: 'Tenkan', type: 'number', default: 9, min: 1, max: 200 },
    { key: 'kijunPeriod', label: 'Kijun', type: 'number', default: 26, min: 1, max: 200 },
    { key: 'senkouBPeriod', label: 'Senkou B', type: 'number', default: 52, min: 1, max: 500 },
    { key: 'displacement', label: 'Displacement', type: 'number', default: 26, min: 1, max: 200 },
  ],
  lines: [
    { key: 'tenkan', label: 'Tenkan-sen', color: '#ef5350', width: 1 },
    { key: 'kijun', label: 'Kijun-sen', color: '#42a5f5', width: 1 },
    {
      key: 'spanA',
      label: 'Senkou Span A',
      color: 'rgba(38,166,154,0.8)',
      width: 1,
      style: 'dashed',
      renderStyle: 'area',
      areaFillColor: 'rgba(38,166,154,0.2)',
    },
    {
      key: 'spanB',
      label: 'Senkou Span B',
      color: 'rgba(239,83,80,0.8)',
      width: 1,
      style: 'dashed',
      renderStyle: 'area',
      areaFillColor: 'rgba(239,83,80,0.2)',
    },
    { key: 'chikou', label: 'Chikou Span', color: '#9c27b0', width: 1 },
  ],
}

/** Rolling midpoint: (highest high + lowest low) / 2 over [i-period+1, i]. */
function midpoint(candles: OHLCV[], i: number, period: number): number | null {
  if (i < period - 1) return null
  let hi = -Infinity
  let lo = Infinity
  for (let j = i - period + 1; j <= i; j++) {
    if (candles[j].h > hi) hi = candles[j].h
    if (candles[j].l < lo) lo = candles[j].l
  }
  return (hi + lo) / 2
}

class Ichimoku extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const tenkanP = Math.max(1, Number(params['tenkanPeriod']) || 9)
    const kijunP = Math.max(1, Number(params['kijunPeriod']) || 26)
    const senkouBP = Math.max(1, Number(params['senkouBPeriod']) || 52)
    const disp = Math.max(1, Number(params['displacement']) || 26)

    const n = candles.length
    const tenkan: (number | null)[] = new Array<number | null>(n).fill(null)
    const kijun: (number | null)[] = new Array<number | null>(n).fill(null)
    const spanA: (number | null)[] = new Array<number | null>(n).fill(null)
    const spanB: (number | null)[] = new Array<number | null>(n).fill(null)
    const chikou: (number | null)[] = new Array<number | null>(n).fill(null)

    // Pre-compute raw tenkan and kijun for all bars (needed for shifted spans)
    const rawTenkan: (number | null)[] = new Array<number | null>(n).fill(null)
    const rawKijun: (number | null)[] = new Array<number | null>(n).fill(null)

    for (let i = 0; i < n; i++) {
      rawTenkan[i] = midpoint(candles, i, tenkanP)
      rawKijun[i] = midpoint(candles, i, kijunP)
      tenkan[i] = rawTenkan[i]
      kijun[i] = rawKijun[i]
    }

    // Senkou Span A at chart position i = (Tenkan[i-disp] + Kijun[i-disp]) / 2
    // This shifts the cloud forward by `disp` bars on the chart
    for (let i = disp; i < n; i++) {
      const t = rawTenkan[i - disp]
      const k = rawKijun[i - disp]
      if (t !== null && k !== null) spanA[i] = (t + k) / 2
    }

    // Senkou Span B at chart position i = midpoint(senkouBP) from bar i-disp
    for (let i = disp; i < n; i++) {
      spanB[i] = midpoint(candles, i - disp, senkouBP)
    }

    // Chikou Span: close of bar i+disp displayed at bar i (look-ahead, null for last `disp` bars)
    for (let i = 0; i < n - disp; i++) {
      chikou[i] = candles[i + disp].c
    }

    return {
      lines: [
        { key: 'tenkan', values: tenkan },
        { key: 'kijun', values: kijun },
        { key: 'spanA', values: spanA },
        { key: 'spanB', values: spanB },
        { key: 'chikou', values: chikou },
      ],
    }
  }
}

registerIndicator(Ichimoku, CONFIG)
